/**
 * Inbox — the triage queue for invoices and Leistungsnachweise that arrive by
 * email. It sits BEFORE the invoice database: nothing is booked until the user
 * decides what to do with it.
 */

export type InboxItemKind = 'invoice' | 'service_description' | 'other'

export type InboxItemState = 'new' | 'accepted' | 'sent_to_flixpector' | 'dismissed'

export type FlixpectorKind = 'legal_audit' | 'service_description'

export interface InboxItem {
  id: string
  kind: InboxItemKind
  state: InboxItemState

  messageId: string
  fromAddress: string | null
  /** The law firm, recovered from the forwarded original */
  originalSender: string | null
  subject: string | null
  receivedAt: string | null
  bodyPreview: string | null

  documentId: string | null
  filename: string | null
  mimeType: string | null
  size: number | null

  supplierName: string | null
  invoiceNumber: string | null
  invoiceDate: string | null
  totalGross: number | null
  currency: string | null
  confidence: string | null
  warnings: string[] | null

  invoiceId: string | null
  flixpectorId: string | null
  flixpectorKind: FlixpectorKind | null
  error: string | null

  createdAt: string
  updatedAt: string
}

export interface InboxListResponse {
  items: InboxItem[]
  pending: number
}

export interface SpreadsheetPreview {
  filename: string | null
  sheetNames: string[]
  sheetName: string
  rows: Array<Array<string | number | null>>
  truncated: boolean
  totalRows: number
}

export const INBOX_KIND_LABELS: Record<InboxItemKind, string> = {
  invoice: 'Rechnung',
  service_description: 'Leistungsnachweis',
  other: 'Mail ohne Beleg',
}

export const INBOX_KIND_COLORS: Record<InboxItemKind, 'primary' | 'secondary' | 'default'> = {
  invoice: 'primary',
  service_description: 'secondary',
  other: 'default',
}

export const INBOX_STATE_LABELS: Record<InboxItemState, string> = {
  new: 'Offen',
  accepted: 'In der Datenbank',
  sent_to_flixpector: 'Bei Flixpector',
  dismissed: 'Erledigt',
}

/** Only spreadsheets need the server-side table preview; PDFs embed directly. */
export function isSpreadsheet(item: InboxItem): boolean {
  const mime = (item.mimeType || '').toLowerCase()
  return (
    mime.includes('spreadsheet') ||
    mime.includes('ms-excel') ||
    mime.includes('csv') ||
    /\.(xlsx|xlsm|xls|ods|csv)$/i.test(item.filename || '')
  )
}

export function isPdf(item: InboxItem): boolean {
  return (
    (item.mimeType || '').toLowerCase() === 'application/pdf' ||
    /\.pdf$/i.test(item.filename || '')
  )
}
